import type { LucideIcon } from "lucide-react";
import {
  AlignHorizontalDistributeCenterIcon,
  ArrowDownUpIcon,
  BarChart3Icon,
  CircleIcon,
  ClockIcon,
  DatabaseIcon,
  DownloadIcon,
  FileTextIcon,
  FlaskConicalIcon,
  FolderInputIcon,
  FolderOutputIcon,
  FolderPlusIcon,
  GlobeIcon,
  GridIcon,
  ImageIcon,
  LayersIcon,
  LinkIcon,
  LockIcon,
  LogInIcon,
  NetworkIcon,
  PaletteIcon,
  PencilIcon,
  ScissorsIcon,
  SearchIcon,
  TagIcon,
  TextCursorIcon,
  Trash2Icon,
  UploadIcon,
} from "lucide-react";

export type FeatureCategory =
  | "core"
  | "sequences"
  | "viewers"
  | "alignments"
  | "creation"
  | "analysis"
  | "files-and-storage"
  | "reference";

export type FeatureEntry = {
  id: string;
  label: string;
  category: FeatureCategory;
  icon: LucideIcon;
  keywords: string[];
  addedInVersion: string;
  helpPath: string;
};

const iconMap: Record<string, LucideIcon> = {
  AlignHorizontalDistributeCenterIcon,
  ArrowDownUpIcon,
  BarChart3Icon,
  CircleIcon,
  ClockIcon,
  DatabaseIcon,
  DownloadIcon,
  FileTextIcon,
  FlaskConicalIcon,
  FolderInputIcon,
  FolderOutputIcon,
  FolderPlusIcon,
  GlobeIcon,
  GridIcon,
  ImageIcon,
  LayersIcon,
  LinkIcon,
  LockIcon,
  LogInIcon,
  NetworkIcon,
  PaletteIcon,
  PencilIcon,
  ScissorsIcon,
  SearchIcon,
  TagIcon,
  TextCursorIcon,
  Trash2Icon,
  UploadIcon,
};

type MdxFrontmatter = {
  label: string;
  category: FeatureCategory;
  icon: string;
  keywords: string[];
  addedInVersion: string;
};

const contentModules = import.meta.glob<MdxFrontmatter>(
  "../help/content/*.mdx",
  { eager: true, import: "frontmatter" },
);

function buildRegistry(): FeatureEntry[] {
  const entries: FeatureEntry[] = [];

  for (const [path, frontmatter] of Object.entries(contentModules)) {
    if (!frontmatter?.label || !frontmatter?.category) continue;

    const icon = iconMap[frontmatter.icon];
    if (!icon) continue;

    const filename = path.split("/").pop();
    if (!filename) continue;

    const id = filename.replace(".mdx", "");

    entries.push({
      id,
      label: frontmatter.label,
      category: frontmatter.category,
      icon,
      keywords: frontmatter.keywords ?? [],
      addedInVersion: frontmatter.addedInVersion ?? "0.0.0",
      helpPath: `/help/${id}`,
    });
  }

  entries.sort((a, b) => a.label.localeCompare(b.label));

  return entries;
}

export const featureRegistry: FeatureEntry[] = buildRegistry();

export const featureMap = new Map<string, FeatureEntry>(
  featureRegistry.map((f) => [f.id, f]),
);

export const categoryOrder: FeatureCategory[] = [
  "core",
  "sequences",
  "viewers",
  "alignments",
  "creation",
  "analysis",
  "files-and-storage",
  "reference",
];

export const categoryLabels: Record<FeatureCategory, string> = {
  core: "Core",
  sequences: "Sequences",
  viewers: "Viewers",
  alignments: "Alignments",
  creation: "Creation",
  analysis: "Analysis",
  "files-and-storage": "Files & Storage",
  reference: "Reference",
};

export type HelpCategory = {
  id: FeatureCategory;
  label: string;
  topics: FeatureEntry[];
};

export const helpCategories: HelpCategory[] = categoryOrder
  .map((category) => ({
    id: category,
    label: categoryLabels[category],
    topics: featureRegistry.filter((t) => t.category === category),
  }))
  .filter((c) => c.topics.length > 0);
